export interface IDataLogReportRow {
  label: string;
  value: string;
}

export interface IDataLogReportImage {
  url: string;
  caption: string;
}

export interface IDataLogReportEntry {
  categoryLabel: string;
  fieldLabel: string;
  cropLabel: string;
  heading: string;
  body: string;
  primaryDateLabel: string;
  rows: IDataLogReportRow[];
  images?: IDataLogReportImage[];
}

export interface IDataLogPdfFilters {
  search: string;
  dataPointType: string;
  dateRange: string;
}

export interface IDataLogPdfContext {
  farmName: string;
  farmerName: string;
  province: string;
}

export interface IDataLogPdfReportOptions {
  title: string;
  subtitle: string;
  generatedAt: string;
  summary: string;
  filters: IDataLogPdfFilters;
  context: IDataLogPdfContext;
  entries: IDataLogReportEntry[];
}

const PAGE_WIDTH = 595.28;
const PAGE_HEIGHT = 841.89;
const PAGE_MARGIN_X = 34;
const PAGE_BOTTOM_LIMIT = 808;
const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN_X * 2;
const IMAGE_COLUMNS = 3;
const IMAGE_CELL_WIDTH = 166;
const IMAGE_FRAME_WIDTH = 152;
const IMAGE_FRAME_HEIGHT = 120;
const IMAGE_MAX_WIDTH = 142;
const IMAGE_MAX_HEIGHT = 98;
const IMAGE_ROW_HEIGHT = 128;

const COLOR_DARK: [number, number, number] = [0.013, 0.2, 0.2];
const COLOR_MUTED: [number, number, number] = [0.33, 0.42, 0.4];
const COLOR_LIGHT_BG: [number, number, number] = [0.96, 0.98, 0.97];
const COLOR_BORDER: [number, number, number] = [0.84, 0.89, 0.88];
const COLOR_WHITE: [number, number, number] = [1, 1, 1];

interface IPreparedRow {
  labelLines: string[];
  valueLines: string[];
  lineCount: number;
}

interface IPreparedEntryImage {
  name: string;
  width: number;
  height: number;
}

interface IPreparedEntry {
  chipsLine: string;
  headingLines: string[];
  bodyLines: string[];
  preparedRows: IPreparedRow[];
  preparedImages: IPreparedEntryImage[];
  hasUnresolvedImages: boolean;
  height: number;
}

interface IPreparedPage {
  commands: string[];
  cursorY: number;
  imageNames: Set<string>;
}

interface IPdfPageBuild {
  stream: string;
  imageNames: string[];
}

interface IResolvedReportImage {
  name: string;
  url: string;
  width: number;
  height: number;
  bytes: Uint8Array;
}

function lshSanitizeText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[^\x20-\x7E]/g, '')
    .trim();
}

function lshEscapePdfText(value: string): string {
  return lshSanitizeText(value)
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');
}

function lshEstimateTextWidth(text: string, fontSize: number): number {
  return lshSanitizeText(text).length * fontSize * 0.52;
}

function lshWrapText(
  text: string,
  maxWidth: number,
  fontSize: number,
): string[] {
  const normalized = lshSanitizeText(text);
  if (!normalized) return [];

  const words = normalized.split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (!currentLine || lshEstimateTextWidth(candidate, fontSize) <= maxWidth) {
      currentLine = candidate;
      return;
    }

    lines.push(currentLine);
    currentLine = word;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

function lshSetFillColor([r, g, b]: [number, number, number]) {
  return `${r} ${g} ${b} rg`;
}

function lshSetStrokeColor([r, g, b]: [number, number, number]) {
  return `${r} ${g} ${b} RG`;
}

function lshDrawRect(
  commands: string[],
  x: number,
  topY: number,
  width: number,
  height: number,
  fillColor?: [number, number, number],
  strokeColor?: [number, number, number],
) {
  const pdfY = PAGE_HEIGHT - topY - height;

  if (fillColor && strokeColor) {
    commands.push(
      `${lshSetFillColor(fillColor)} ${lshSetStrokeColor(
        strokeColor,
      )} ${x} ${pdfY} ${width} ${height} re B`,
    );
    return;
  }

  if (fillColor) {
    commands.push(
      `${lshSetFillColor(fillColor)} ${x} ${pdfY} ${width} ${height} re f`,
    );
    return;
  }

  if (strokeColor) {
    commands.push(
      `${lshSetStrokeColor(strokeColor)} ${x} ${pdfY} ${width} ${height} re S`,
    );
  }
}

function lshDrawLine(
  commands: string[],
  x1: number,
  topY1: number,
  x2: number,
  topY2: number,
  strokeColor: [number, number, number],
  lineWidth = 1,
) {
  const y1 = PAGE_HEIGHT - topY1;
  const y2 = PAGE_HEIGHT - topY2;
  commands.push(
    `${lineWidth} w ${lshSetStrokeColor(
      strokeColor,
    )} ${x1} ${y1} m ${x2} ${y2} l S`,
  );
}

function lshDrawText(
  commands: string[],
  text: string,
  x: number,
  topY: number,
  fontSize: number,
  font: 'F1' | 'F2',
  color: [number, number, number],
) {
  const escaped = lshEscapePdfText(text);
  if (!escaped) return;

  const y = PAGE_HEIGHT - topY;
  commands.push(
    `BT /${font} ${fontSize} Tf ${lshSetFillColor(
      color,
    )} 1 0 0 1 ${x} ${y} Tm (${escaped}) Tj ET`,
  );
}

function lshDrawImage(
  page: IPreparedPage,
  imageName: string,
  x: number,
  topY: number,
  width: number,
  height: number,
) {
  const y = PAGE_HEIGHT - topY - height;
  page.imageNames.add(imageName);
  page.commands.push(
    `q ${width} 0 0 ${height} ${x} ${y} cm /${imageName} Do Q`,
  );
}

function lshBuildHeader(
  page: IPreparedPage,
  options: IDataLogPdfReportOptions,
  pageNumber: number,
) {
  lshDrawRect(page.commands, 0, 0, PAGE_WIDTH, 78, COLOR_DARK);
  lshDrawText(
    page.commands,
    options.title,
    PAGE_MARGIN_X,
    29,
    16,
    'F2',
    COLOR_WHITE,
  );
  lshDrawText(
    page.commands,
    `Generated ${options.generatedAt}`,
    PAGE_MARGIN_X,
    48,
    9,
    'F1',
    [0.82, 0.91, 0.89],
  );
  lshDrawText(
    page.commands,
    `Page ${pageNumber}`,
    PAGE_WIDTH - PAGE_MARGIN_X - 48,
    48,
    9,
    'F1',
    [0.82, 0.91, 0.89],
  );
  page.cursorY = 96;
}

function lshFitImage(width: number, height: number) {
  const fitScale = Math.min(
    IMAGE_MAX_WIDTH / width,
    IMAGE_MAX_HEIGHT / height,
    1,
  );
  return {
    width: Math.max(18, Math.round(width * fitScale)),
    height: Math.max(18, Math.round(height * fitScale)),
  };
}

function lshPrepareEntry(
  entry: IDataLogReportEntry,
  resolvedImages: Map<string, IResolvedReportImage>,
): IPreparedEntry {
  const chipsParts = [entry.categoryLabel, entry.fieldLabel];
  if (entry.cropLabel) chipsParts.push(entry.cropLabel);

  const headingLines = lshWrapText(entry.heading, CONTENT_WIDTH - 28, 11);
  const bodyLines = entry.body
    ? lshWrapText(entry.body, CONTENT_WIDTH - 28, 10)
    : [];

  const preparedRows = entry.rows.map((row) => {
    const labelLines = lshWrapText(row.label, 145, 9);
    const valueLines = lshWrapText(row.value, CONTENT_WIDTH - 190, 9);
    const lineCount = Math.max(labelLines.length || 1, valueLines.length || 1);
    return {labelLines, valueLines, lineCount};
  });

  const preparedImages = (entry.images || [])
    .map((item) => resolvedImages.get(item.url))
    .filter((item): item is IResolvedReportImage => Boolean(item))
    .map((item) => {
      const fitted = lshFitImage(item.width, item.height);
      return {
        name: item.name,
        width: fitted.width,
        height: fitted.height,
      };
    });

  let height = 16;
  height += 13;
  height += 12;
  height += Math.max(1, headingLines.length) * 13;

  if (bodyLines.length) {
    height += 7 + bodyLines.length * 11;
  }

  if (preparedImages.length) {
    const imageRows = Math.ceil(preparedImages.length / IMAGE_COLUMNS);
    height += 7 + 11;
    height += imageRows * IMAGE_ROW_HEIGHT + 4;
  }

  if (preparedRows.length) {
    height += 8;
    preparedRows.forEach((row) => {
      height += row.lineCount * 10 + 4;
    });
  }

  if (!preparedImages.length && (entry.images || []).length) {
    height += 14;
  }

  height += 14;

  return {
    chipsLine: chipsParts.join(' • '),
    headingLines,
    bodyLines,
    preparedRows,
    preparedImages,
    hasUnresolvedImages: Boolean(
      (entry.images || []).length && !preparedImages.length,
    ),
    height: Math.max(height, 88),
  };
}

function lshBuildPages(
  options: IDataLogPdfReportOptions,
  resolvedImages: Map<string, IResolvedReportImage>,
): IPdfPageBuild[] {
  const pages: IPreparedPage[] = [];

  const lshAddPage = () => {
    const page: IPreparedPage = {
      commands: [],
      cursorY: 0,
      imageNames: new Set<string>(),
    };
    pages.push(page);
    lshBuildHeader(page, options, pages.length);
    return page;
  };

  let currentPage = lshAddPage();

  const lshRenderFilters = () => {
    const boxHeight = 124;
    lshDrawRect(
      currentPage.commands,
      PAGE_MARGIN_X,
      currentPage.cursorY,
      CONTENT_WIDTH,
      boxHeight,
      COLOR_LIGHT_BG,
      COLOR_BORDER,
    );

    const left = PAGE_MARGIN_X + 14;
    let innerY = currentPage.cursorY + 20;

    lshDrawText(
      currentPage.commands,
      options.subtitle,
      left,
      innerY,
      10,
      'F1',
      COLOR_MUTED,
    );

    innerY += 18;
    lshDrawText(
      currentPage.commands,
      `Farm: ${options.context.farmName}`,
      left,
      innerY,
      9,
      'F1',
      COLOR_MUTED,
    );

    innerY += 14;
    lshDrawText(
      currentPage.commands,
      `Farmer: ${options.context.farmerName}`,
      left,
      innerY,
      9,
      'F1',
      COLOR_MUTED,
    );

    innerY += 14;
    lshDrawText(
      currentPage.commands,
      `Province: ${options.context.province}`,
      left,
      innerY,
      9,
      'F1',
      COLOR_MUTED,
    );

    innerY += 16;
    lshDrawText(
      currentPage.commands,
      `Summary: ${options.summary}`,
      left,
      innerY,
      10,
      'F2',
      COLOR_DARK,
    );

    innerY += 17;
    lshDrawText(
      currentPage.commands,
      `Filters: Type ${options.filters.dataPointType} | Date ${
        options.filters.dateRange
      } | Search ${options.filters.search || 'None'}`,
      left,
      innerY,
      9,
      'F1',
      COLOR_MUTED,
    );

    currentPage.cursorY += boxHeight + 16;
  };

  lshRenderFilters();

  if (!options.entries.length) {
    lshDrawRect(
      currentPage.commands,
      PAGE_MARGIN_X,
      currentPage.cursorY,
      CONTENT_WIDTH,
      70,
      [1, 1, 1],
      COLOR_BORDER,
    );
    lshDrawText(
      currentPage.commands,
      'No matching data logs for the selected filters.',
      PAGE_MARGIN_X + 14,
      currentPage.cursorY + 36,
      11,
      'F2',
      COLOR_DARK,
    );
    return pages.map((page) => ({
      stream: page.commands.join('\n'),
      imageNames: Array.from(page.imageNames),
    }));
  }

  options.entries.forEach((entry) => {
    const prepared = lshPrepareEntry(entry, resolvedImages);

    if (currentPage.cursorY + prepared.height > PAGE_BOTTOM_LIMIT) {
      currentPage = lshAddPage();
    }

    const cardTop = currentPage.cursorY;
    lshDrawRect(
      currentPage.commands,
      PAGE_MARGIN_X,
      cardTop,
      CONTENT_WIDTH,
      prepared.height,
      [1, 1, 1],
      COLOR_BORDER,
    );

    let y = cardTop + 16;
    lshDrawText(
      currentPage.commands,
      prepared.chipsLine,
      PAGE_MARGIN_X + 14,
      y,
      9,
      'F1',
      COLOR_MUTED,
    );
    lshDrawText(
      currentPage.commands,
      entry.primaryDateLabel || 'Date unavailable',
      PAGE_WIDTH - PAGE_MARGIN_X - 145,
      y,
      9,
      'F1',
      COLOR_MUTED,
    );

    y += 17;
    prepared.headingLines.forEach((line) => {
      lshDrawText(
        currentPage.commands,
        line,
        PAGE_MARGIN_X + 14,
        y,
        11,
        'F2',
        COLOR_DARK,
      );
      y += 13;
    });

    if (prepared.bodyLines.length) {
      y += 2;
      prepared.bodyLines.forEach((line) => {
        lshDrawText(
          currentPage.commands,
          line,
          PAGE_MARGIN_X + 14,
          y,
          10,
          'F1',
          COLOR_DARK,
        );
        y += 11;
      });
    }

    if (prepared.preparedImages.length) {
      y += 7;
      lshDrawText(
        currentPage.commands,
        'Images',
        PAGE_MARGIN_X + 14,
        y,
        9,
        'F2',
        COLOR_DARK,
      );
      y += 11;

      prepared.preparedImages.forEach((image, imageIndex) => {
        const rowIndex = Math.floor(imageIndex / IMAGE_COLUMNS);
        const colIndex = imageIndex % IMAGE_COLUMNS;
        const frameX = PAGE_MARGIN_X + 14 + colIndex * IMAGE_CELL_WIDTH;
        const frameY = y + rowIndex * IMAGE_ROW_HEIGHT;
        const imageX = frameX + (IMAGE_FRAME_WIDTH - image.width) / 2;
        const imageY = frameY + 4 + (IMAGE_MAX_HEIGHT - image.height) / 2;

        lshDrawRect(
          currentPage.commands,
          frameX,
          frameY,
          IMAGE_FRAME_WIDTH,
          IMAGE_FRAME_HEIGHT,
          [0.985, 0.99, 0.989],
          COLOR_BORDER,
        );
        lshDrawImage(
          currentPage,
          image.name,
          imageX,
          imageY,
          image.width,
          image.height,
        );
        lshDrawText(
          currentPage.commands,
          `Image ${imageIndex + 1}`,
          frameX + 4,
          frameY + IMAGE_FRAME_HEIGHT - 8,
          7,
          'F1',
          COLOR_MUTED,
        );
      });

      const imageRows = Math.ceil(
        prepared.preparedImages.length / IMAGE_COLUMNS,
      );
      y += imageRows * IMAGE_ROW_HEIGHT + 4;
    } else if (prepared.hasUnresolvedImages) {
      y += 10;
      lshDrawText(
        currentPage.commands,
        'Images are attached but could not be embedded in this PDF.',
        PAGE_MARGIN_X + 14,
        y,
        9,
        'F1',
        COLOR_MUTED,
      );
      y += 14;
    }

    if (prepared.preparedRows.length) {
      y += 4;
      prepared.preparedRows.forEach((row) => {
        for (let index = 0; index < row.lineCount; index += 1) {
          const labelLine = row.labelLines[index] || '';
          const valueLine = row.valueLines[index] || '';

          if (labelLine) {
            lshDrawText(
              currentPage.commands,
              labelLine,
              PAGE_MARGIN_X + 14,
              y,
              9,
              'F1',
              COLOR_MUTED,
            );
          }

          if (valueLine) {
            lshDrawText(
              currentPage.commands,
              valueLine,
              PAGE_MARGIN_X + 168,
              y,
              9,
              'F1',
              COLOR_DARK,
            );
          }

          y += 10;
        }
        y += 4;
      });
    }

    currentPage.cursorY += prepared.height + 12;

    if (currentPage.cursorY <= PAGE_BOTTOM_LIMIT) {
      lshDrawLine(
        currentPage.commands,
        PAGE_MARGIN_X + 3,
        currentPage.cursorY - 3,
        PAGE_WIDTH - PAGE_MARGIN_X - 3,
        currentPage.cursorY - 3,
        [0.89, 0.92, 0.91],
        0.6,
      );
    }
  });

  return pages.map((page) => ({
    stream: page.commands.join('\n'),
    imageNames: Array.from(page.imageNames),
  }));
}

function lshToBytes(value: string): Uint8Array {
  return new TextEncoder().encode(value);
}

function lshConcatBytes(chunks: Uint8Array[]): Uint8Array {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;

  chunks.forEach((chunk) => {
    merged.set(chunk, offset);
    offset += chunk.length;
  });

  return merged;
}

function lshBuildStreamObject(stream: Uint8Array): Uint8Array {
  return lshConcatBytes([
    lshToBytes(`<< /Length ${stream.length} >>\nstream\n`),
    stream,
    lshToBytes('\nendstream'),
  ]);
}

function lshBuildImageObject(image: IResolvedReportImage): Uint8Array {
  return lshConcatBytes([
    lshToBytes(
      `<< /Type /XObject /Subtype /Image /Width ${image.width} /Height ${image.height} ` +
        '/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode ' +
        `/Length ${image.bytes.length} >>\nstream\n`,
    ),
    image.bytes,
    lshToBytes('\nendstream'),
  ]);
}

function lshBuildPdfDocument(
  pages: IPdfPageBuild[],
  resolvedImages: Map<string, IResolvedReportImage>,
): Uint8Array {
  const imageList = Array.from(resolvedImages.values());
  const imageObjectIdByName: Record<string, number> = {};
  const pageObjectIds: number[] = [];
  const contentObjectIds: number[] = [];
  let nextId = 5;

  imageList.forEach((image) => {
    imageObjectIdByName[image.name] = nextId;
    nextId += 1;
  });

  pages.forEach(() => {
    pageObjectIds.push(nextId);
    contentObjectIds.push(nextId + 1);
    nextId += 2;
  });

  const totalObjects = nextId - 1;
  const objects: Array<Uint8Array | null> = new Array(totalObjects + 1).fill(
    null,
  );

  objects[1] = lshToBytes('<< /Type /Catalog /Pages 2 0 R >>');
  objects[3] = lshToBytes(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  );
  objects[4] = lshToBytes(
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>',
  );

  imageList.forEach((image) => {
    const objectId = imageObjectIdByName[image.name];
    objects[objectId] = lshBuildImageObject(image);
  });

  pages.forEach((page, pageIndex) => {
    const pageObjectId = pageObjectIds[pageIndex];
    const contentObjectId = contentObjectIds[pageIndex];
    objects[contentObjectId] = lshBuildStreamObject(lshToBytes(page.stream));

    const imageResourceEntries = page.imageNames
      .map((name) => `/${name} ${imageObjectIdByName[name]} 0 R`)
      .join(' ');
    const imageResourceBlock = imageResourceEntries
      ? ` /XObject << ${imageResourceEntries} >>`
      : '';

    const pageObject =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] ` +
      `/Resources << /Font << /F1 3 0 R /F2 4 0 R >>${imageResourceBlock} >> ` +
      `/Contents ${contentObjectId} 0 R >>`;
    objects[pageObjectId] = lshToBytes(pageObject);
  });

  objects[2] = lshToBytes(
    `<< /Type /Pages /Count ${pages.length} /Kids [` +
      pageObjectIds.map((id) => `${id} 0 R`).join(' ') +
      '] >>',
  );

  const outputChunks: Uint8Array[] = [lshToBytes('%PDF-1.4\n')];
  const offsets = new Array(totalObjects + 1).fill(0);
  let byteOffset = outputChunks[0].length;

  for (let objectId = 1; objectId <= totalObjects; objectId += 1) {
    const objectBody = objects[objectId] || lshToBytes('');
    const chunk = lshConcatBytes([
      lshToBytes(`${objectId} 0 obj\n`),
      objectBody,
      lshToBytes('\nendobj\n'),
    ]);
    offsets[objectId] = byteOffset;
    outputChunks.push(chunk);
    byteOffset += chunk.length;
  }

  const xrefStart = byteOffset;
  let xref = `xref\n0 ${totalObjects + 1}\n`;
  xref += '0000000000 65535 f \n';
  for (let objectId = 1; objectId <= totalObjects; objectId += 1) {
    const offset = String(offsets[objectId]).padStart(10, '0');
    xref += `${offset} 00000 n \n`;
  }
  xref += `trailer\n<< /Size ${totalObjects + 1} /Root 1 0 R >>\n`;
  xref += `startxref\n${xrefStart}\n%%EOF`;
  outputChunks.push(lshToBytes(xref));

  return lshConcatBytes(outputChunks);
}

function lshBase64ToBytes(base64: string): Uint8Array {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

function lshLoadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = src;
  });
}

async function lshConvertImageUrlToJpeg(
  url: string,
): Promise<{bytes: Uint8Array; width: number; height: number} | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    const localUrl = window.URL.createObjectURL(blob);

    try {
      const image = await lshLoadImageElement(localUrl);
      const width = Math.max(
        1,
        Math.round(image.naturalWidth || image.width || 1),
      );
      const height = Math.max(
        1,
        Math.round(image.naturalHeight || image.height || 1),
      );

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        return null;
      }

      context.fillStyle = '#FFFFFF';
      context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);

      const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.82);
      const base64 = jpegDataUrl.split(',')[1] || '';
      if (!base64) return null;

      return {
        bytes: lshBase64ToBytes(base64),
        width,
        height,
      };
    } finally {
      window.URL.revokeObjectURL(localUrl);
    }
  } catch (error) {
    console.error('Failed to convert report image', {url, error});
    return null;
  }
}

function lshGetUniqueReportImages(
  entries: IDataLogReportEntry[],
): IDataLogReportImage[] {
  const seen = new Set<string>();
  const unique: IDataLogReportImage[] = [];

  entries.forEach((entry) => {
    (entry.images || []).forEach((image) => {
      const normalizedUrl = image.url.trim();
      if (!normalizedUrl || seen.has(normalizedUrl)) return;
      seen.add(normalizedUrl);
      unique.push({
        url: normalizedUrl,
        caption: image.caption?.trim() || '',
      });
    });
  });

  return unique;
}

async function lshResolveReportImages(
  entries: IDataLogReportEntry[],
): Promise<Map<string, IResolvedReportImage>> {
  const resolved = new Map<string, IResolvedReportImage>();
  const uniqueImages = lshGetUniqueReportImages(entries);

  let imageIndex = 1;
  for (const image of uniqueImages) {
    const jpegImage = await lshConvertImageUrlToJpeg(image.url);
    if (!jpegImage) continue;

    const resolvedImage: IResolvedReportImage = {
      name: `Im${imageIndex}`,
      url: image.url,
      width: jpegImage.width,
      height: jpegImage.height,
      bytes: jpegImage.bytes,
    };
    resolved.set(image.url, resolvedImage);
    imageIndex += 1;
  }

  return resolved;
}

function lshDownloadBlob(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export async function lshDownloadDataLogPdfReport(
  options: IDataLogPdfReportOptions,
): Promise<void> {
  const resolvedImages = await lshResolveReportImages(options.entries);
  const pages = lshBuildPages(options, resolvedImages);
  const pdfBytes = lshBuildPdfDocument(pages, resolvedImages);
  const blob = new Blob([pdfBytes], {type: 'application/pdf'});
  const fileDate = new Date().toISOString().slice(0, 10);
  const safeTitle = options.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const fileName = `${safeTitle || 'data-log-report'}-${fileDate}.pdf`;
  lshDownloadBlob(blob, fileName);
}
